import PropTypes from "prop-types";
import { Button, Checkbox, Input, Select, SettingsCard } from "top-component-library";
import {
  ORDER_NUM_DIGITS_OPTIONS,
  ORDER_NUMBER_PREFIX_MAX_LENGTH,
  STATEMENT_DESCRIPTOR_MAX_LENGTH,
} from "../constants";
import { ReceiptPreview, SaveRow, SettingControl, SettingHeading, SettingHint } from "../styles";

const OtherSettingsCard = ({
  orderNumberPrefix,
  setOrderNumberPrefix,
  saveOrderNumberPrefix,
  statementDescriptor,
  setStatementDescriptor,
  saveStatementDescriptor,
  appProperties,
  setAppProperty,
  saveAppProperties,
  sendSmsDrinkReminder,
  setSendSmsDrinkReminder,
  smsFeatureEnabled,
}) => (
  <SettingsCard title="Other Settings">
    <div>
      <SettingHeading>Order number prefix</SettingHeading>
      <SettingHint>
        TOP by DoorDash can display up to 5 letters before the order numbers at this venue. This simplifies customer
        support if you manage multiple venues.
      </SettingHint>
      <SettingControl>
        <label htmlFor="order-number-prefix">Prefix letter(s)</label>
        <Input
          id="order-number-prefix"
          placeholder="Prefix letter(s)"
          maxLength={ORDER_NUMBER_PREFIX_MAX_LENGTH}
          value={orderNumberPrefix}
          onChange={(event) => setOrderNumberPrefix(event.target.value)}
        />
      </SettingControl>
      <SettingHint>
        For example:
        <ReceiptPreview>
          <i>Order</i>
          <b>{orderNumberPrefix}107</b>
          <span>is ready for pickup</span>
        </ReceiptPreview>
      </SettingHint>
      <SaveRow>
        <Button type="primary" onClick={saveOrderNumberPrefix}>
          Save
        </Button>
      </SaveRow>
    </div>

    <div>
      <SettingHeading>Reset Order Number Daily</SettingHeading>
      <SettingHint>Reset the order number back to 1 around 4:00 AM each day.</SettingHint>
      <Checkbox
        checked={Boolean(appProperties.order_num_daily_reset)}
        onChange={(event) => setAppProperty("order_num_daily_reset", event.target.checked)}
      >
        Order Number Daily Reset
      </Checkbox>
      <SaveRow>
        <Button type="primary" onClick={saveAppProperties}>
          Save
        </Button>
      </SaveRow>
    </div>

    <div>
      <SettingHeading>Order Number Max Digits</SettingHeading>
      <SettingHint>The maximum number of digits in an order number.</SettingHint>
      <Select
        value={appProperties.order_num_digits ?? 3}
        onChange={(value) => setAppProperty("order_num_digits", value)}
        style={{ width: 120 }}
      >
        {ORDER_NUM_DIGITS_OPTIONS.map((digits) => (
          <Select.Option value={digits} key={digits}>
            {digits}
          </Select.Option>
        ))}
      </Select>
      <SaveRow>
        <Button type="primary" onClick={saveAppProperties}>
          Save
        </Button>
      </SaveRow>
    </div>

    <div>
      <SettingHeading>Card Statements</SettingHeading>
      <SettingHint>
        The name of the charge that appears on guests&apos; credit card statements. It should match your business name,
        or be a shortened version of it. (An unclear name increases the chance of chargebacks.) Limit is 16 characters.
        Some banks may display fewer characters, or may convert to all caps.
      </SettingHint>
      <SettingControl>
        <label htmlFor="statement-descriptor">Descriptor</label>
        <Input
          id="statement-descriptor"
          placeholder="Descriptor"
          maxLength={STATEMENT_DESCRIPTOR_MAX_LENGTH}
          value={statementDescriptor}
          onChange={(event) => setStatementDescriptor(event.target.value)}
        />
      </SettingControl>
      <SettingHint>Card statements will show &quot;BB* {statementDescriptor}&quot;</SettingHint>
      <SaveRow>
        <Button type="primary" onClick={saveStatementDescriptor}>
          Save
        </Button>
      </SaveRow>
    </div>

    {smsFeatureEnabled && (
      <div>
        <SettingHeading>Grab Another Drink SMS Notifications</SettingHeading>
        <SettingHint>Send an SMS to nudge guests to grab another drink before they go.</SettingHint>
        <Checkbox checked={sendSmsDrinkReminder} onChange={(event) => setSendSmsDrinkReminder(event.target.checked)}>
          Grab Another Drink SMS Notifications
        </Checkbox>
        <SaveRow>
          <Button type="primary" onClick={saveAppProperties}>
            Save
          </Button>
        </SaveRow>
      </div>
    )}
  </SettingsCard>
);

OtherSettingsCard.propTypes = {
  orderNumberPrefix: PropTypes.string,
  setOrderNumberPrefix: PropTypes.func.isRequired,
  saveOrderNumberPrefix: PropTypes.func.isRequired,
  statementDescriptor: PropTypes.string,
  setStatementDescriptor: PropTypes.func.isRequired,
  saveStatementDescriptor: PropTypes.func.isRequired,
  appProperties: PropTypes.object.isRequired,
  setAppProperty: PropTypes.func.isRequired,
  saveAppProperties: PropTypes.func.isRequired,
  sendSmsDrinkReminder: PropTypes.bool,
  setSendSmsDrinkReminder: PropTypes.func.isRequired,
  smsFeatureEnabled: PropTypes.bool,
};

export default OtherSettingsCard;
